// frontend/src/features/meetings/components/MeetingDetailModal.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import meetingsApi, {
  isMeetingPast,
  isMeetingInProgress,
  getEffectiveMeetingStatus,
  canJoinMeeting,
  getMsUntilMeetingAccessOpen,
  formatMeetingCountdown,
} from '../api/meetingsApi';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ header: [2, 3, 4, false] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    ['blockquote'],
    ['clean'],
  ],
};

const quillFormats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'header',
  'list',
  'bullet',
  'check',
  'blockquote',
];

function formatNotesInline(text) {
  if (!text) return '';
  let str = text;
  str = str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Bold
  str = str.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic
  str = str.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // Inline code
  str = str.replace(/`(.*?)`/g, '<code>$1</code>');
  return str;
}

function renderPreviewContent(content) {
  if (!content || !content.trim()) return '';
  const trimmed = content.trim();
  // Check if content contains HTML tags (from Quill)
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(trimmed);
  if (hasHtmlTags) {
    return trimmed;
  }
  // Fallback to markdown renderer for legacy plain text / markdown notes
  return renderNotesMarkdown(trimmed);
}

function renderNotesMarkdown(md) {
  if (!md) return '';
  const lines = md.split('\n');
  const htmlParts = [];
  let inUl = false;
  let inOl = false;
  let inBlockquote = false;

  const closeOpenLists = () => {
    if (inUl) {
      htmlParts.push('</ul>');
      inUl = false;
    }
    if (inOl) {
      htmlParts.push('</ol>');
      inOl = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    // Checklist item [ ] or [x]
    if (/^\[([ xX])\]\s+/.test(trimmed)) {
      closeOpenLists();
      const isChecked = trimmed.charAt(1).toLowerCase() === 'x';
      const text = trimmed.replace(/^\[([ xX])\]\s+/, '');
      htmlParts.push(
        `<div class="cal-notes-check-item ${isChecked ? 'checked' : ''}"><span class="cal-notes-check-box">${isChecked ? '☑' : '☐'}</span> <span>${formatNotesInline(text)}</span></div>`
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      if (inUl) {
        htmlParts.push('</ul>');
        inUl = false;
      }
      if (!inOl) {
        inOl = true;
        htmlParts.push('<ol class="cal-notes-ol">');
      }
      const text = trimmed.replace(/^\d+\.\s+/, '');
      htmlParts.push(`<li>${formatNotesInline(text)}</li>`);
      continue;
    } else if (inOl) {
      htmlParts.push('</ol>');
      inOl = false;
    }

    // Bullet list
    if (trimmed.startsWith('• ') || trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (inOl) {
        htmlParts.push('</ol>');
        inOl = false;
      }
      if (!inUl) {
        inUl = true;
        htmlParts.push('<ul class="cal-notes-ul">');
      }
      const text = trimmed.replace(/^[•\-\*]\s+/, '');
      htmlParts.push(`<li>${formatNotesInline(text)}</li>`);
      continue;
    } else if (inUl) {
      htmlParts.push('</ul>');
      inUl = false;
    }

    // Blockquote / Key takeaway
    if (trimmed.startsWith('>')) {
      if (!inBlockquote) {
        inBlockquote = true;
        htmlParts.push('<blockquote class="cal-notes-blockquote">');
      }
      htmlParts.push(`<p>${formatNotesInline(trimmed.replace(/^>\s*/, ''))}</p>`);
      continue;
    } else if (inBlockquote) {
      inBlockquote = false;
      htmlParts.push('</blockquote>');
    }

    // Headings
    if (trimmed.startsWith('# ')) {
      closeOpenLists();
      htmlParts.push(`<h4>${formatNotesInline(trimmed.slice(2))}</h4>`);
    } else if (trimmed.startsWith('## ')) {
      closeOpenLists();
      htmlParts.push(`<h5>${formatNotesInline(trimmed.slice(3))}</h5>`);
    } else if (trimmed.startsWith('### ')) {
      closeOpenLists();
      htmlParts.push(`<h6>${formatNotesInline(trimmed.slice(4))}</h6>`);
    } else if (trimmed === '') {
      closeOpenLists();
      htmlParts.push('<div class="cal-notes-preview-spacer"></div>');
    } else {
      closeOpenLists();
      htmlParts.push(`<p>${formatNotesInline(rawLine)}</p>`);
    }
  }

  closeOpenLists();
  if (inBlockquote) htmlParts.push('</blockquote>');

  return htmlParts.join('\n');
}

const normalizeRoleTier = (role = '') => {
  if (!role) return 'member';
  const clean = String(role)
    .trim()
    .toLowerCase()
    .replace(/^org_/, '')
    .replace(/[\s-]+/g, '_');
  if (['owner'].includes(clean) || clean.includes('owner')) return 'owner';
  if (['admin', 'superadmin'].includes(clean) || clean.includes('admin')) return 'admin';
  if (
    [
      'manager',
      'sales_manager',
      'sales_lead',
      'lead',
      'director',
      'head',
      'sales_director',
      'sales_head',
    ].includes(clean) ||
    clean.includes('manager') ||
    clean.includes('lead') ||
    clean.includes('director') ||
    clean.includes('head')
  ) {
    return 'manager';
  }
  if (clean.includes('viewer')) return 'viewer';
  if (clean.includes('researcher')) return 'researcher';
  return 'member';
};

export const getRoleTierLevel = (role = '') => {
  const normalized = normalizeRoleTier(role);
  switch (normalized) {
    case 'owner':
      return 4;
    case 'admin':
      return 3;
    case 'manager':
      return 2;
    case 'member':
    case 'researcher':
      return 1;
    case 'viewer':
      return 0;
    default:
      return 1;
  }
};

export default function MeetingDetailModal({
  meeting,
  initialTab = 'info',
  hosts = [],
  currentUser = null,
  currentUserRole = '',
  isOwner = false,
  onClose,
  onSaveRecap,
  onReschedule,
  onCancelMeeting,
  onDeleteMeeting,
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [copiedLink, setCopiedLink] = useState(false);
  const initialNotes =
    meeting?.postMeetingRecap?.repNotes ||
    meeting?.post_meeting_recap?.repNotes ||
    meeting?.notes ||
    meeting?.repNotes ||
    '';
  const [repNotes, setRepNotes] = useState(initialNotes);
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const userRoleLevel = useMemo(() => {
    if (isOwner) return 4;
    if (currentUserRole) return getRoleTierLevel(currentUserRole);
    if (currentUser?.role || currentUser?.workspace_role) {
      return getRoleTierLevel(currentUser?.role || currentUser?.workspace_role);
    }
    // Default fallback to 2 for backward compatibility
    return 2;
  }, [isOwner, currentUserRole, currentUser]);

  const isViewer = userRoleLevel === 0;
  const isPrivileged = userRoleLevel >= 2;

  const isHostOrCreator = useMemo(() => {
    if (!currentUser && !currentUserRole) return true;
    const userEmail = (currentUser?.email || '').toLowerCase();
    const userId = String(currentUser?.id || currentUser?.userId || '');
    const hostEmail = (meeting?.hostEmail || '').toLowerCase();
    const hostId = String(meeting?.hostId || meeting?.host_id || meeting?.userId || '');
    const createdBy = String(meeting?.createdBy || meeting?.created_by || '');

    if (userEmail && hostEmail && userEmail === hostEmail) return true;
    if (userId && hostId && userId === hostId) return true;
    if (userId && createdBy && userId === createdBy) return true;
    return false;
  }, [currentUser, currentUserRole, meeting]);

  const canManageMeeting = !isViewer && (isPrivileged || isHostOrCreator);
  const canEditRecap = !isViewer;
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [saveNotesSuccess, setSaveNotesSuccess] = useState(false);
  const [notesViewMode, setNotesViewMode] = useState(() => {
    if (isViewer) return 'preview';
    const stripped = initialNotes
      ? initialNotes
          .replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/g, ' ')
          .trim()
      : '';
    return stripped.length > 0 ? 'preview' : 'write';
  });

  const notesWordCount = useMemo(() => {
    if (!repNotes) return 0;
    const stripped = repNotes
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return stripped ? stripped.split(/\s+/).filter(Boolean).length : 0;
  }, [repNotes]);

  const hasNotesContent = useMemo(() => {
    if (!repNotes) return false;
    const stripped = repNotes
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return stripped.length > 0;
  }, [repNotes]);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [showRescheduleLockedPrompt, setShowRescheduleLockedPrompt] = useState(false);
  const [liveCampaignObjective, setLiveCampaignObjective] = useState(null);

  const initialBrief =
    meeting?.preMeetingBrief?.aiBrief ||
    meeting?.pre_meeting_brief?.aiBrief ||
    (typeof meeting?.preMeetingBrief === 'object' && meeting?.preMeetingBrief?.executiveSummary
      ? meeting.preMeetingBrief
      : null);
  const [aiBrief, setAiBrief] = useState(initialBrief);
  const [isGeneratingAiBrief, setIsGeneratingAiBrief] = useState(false);
  const [aiBriefError, setAiBriefError] = useState(null);
  const [briefSection, setBriefSection] = useState('all');
  const [copiedCta, setCopiedCta] = useState(false);
  const [copiedFullBrief, setCopiedFullBrief] = useState(false);
  const [briefChecklistDone, setBriefChecklistDone] = useState({});

  useEffect(() => {
    let isMounted = true;
    const targetCmpId =
      meeting?.campaignId ||
      meeting?.campaign_id ||
      meeting?.preMeetingBrief?.campaignId ||
      meeting?.preMeetingBrief?.campaign_id;

    const targetCmpName =
      meeting?.campaignName ||
      meeting?.campaign_name ||
      meeting?.preMeetingBrief?.campaignName ||
      meeting?.preMeetingBrief?.campaign_name;

    const wsId = meeting?.workspaceId || meeting?.workspace_id;

    if (!targetCmpId && !targetCmpName) return;

    if (meetingsApi && typeof meetingsApi.getWorkspaceCampaigns === 'function') {
      try {
        const res = meetingsApi.getWorkspaceCampaigns(wsId);
        if (res && typeof res.then === 'function') {
          res
            .then((cmps) => {
              if (!isMounted || !Array.isArray(cmps)) return;
              const found = cmps.find(
                (c) =>
                  (targetCmpId && String(c.id).trim() === String(targetCmpId).trim()) ||
                  (targetCmpName &&
                    c.name &&
                    (c.name.toLowerCase() === targetCmpName.toLowerCase() ||
                      targetCmpName.toLowerCase().includes(c.name.toLowerCase()) ||
                      c.name.toLowerCase().includes(targetCmpName.toLowerCase())))
              );
              if (found && found.objective) {
                setLiveCampaignObjective(found.objective);
              }
            })
            .catch((err) => {
              console.warn('Failed to fetch live campaign objective from database:', err);
            });
        }
      } catch (err) {
        console.warn('Failed to call getWorkspaceCampaigns:', err);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [meeting]);

  const attendeesList = useMemo(() => {
    if (!meeting) return [];

    if (Array.isArray(meeting.attendees) && meeting.attendees.length > 0) {
      return meeting.attendees;
    }
    if (
      Array.isArray(meeting.preMeetingBrief?.attendees) &&
      meeting.preMeetingBrief.attendees.length > 0
    ) {
      return meeting.preMeetingBrief.attendees;
    }
    if (Array.isArray(meeting.attendeeList) && meeting.attendeeList.length > 0) {
      return meeting.attendeeList;
    }

    // Check if guestEmail has comma separated emails or guestName indicates group attendees
    if (meeting.guestEmail && meeting.guestEmail.includes(',')) {
      const emailList = meeting.guestEmail
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
      const companyList = (meeting.guestCompany || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);

      // Check if guestName contains multiple comma-separated names
      const nameList = (meeting.guestName || '').includes(',')
        ? meeting.guestName
            .split(',')
            .map((n) => n.trim())
            .filter(Boolean)
        : [];

      return emailList.map((email, idx) => {
        let name = nameList[idx];
        if (!name) {
          // If first entry and guestName had "(Name +N)", extract first name
          if (idx === 0 && meeting.guestName && meeting.guestName.includes('(')) {
            const match = meeting.guestName.match(/\(([^)+]+)/);
            if (match && match[1]) {
              name = match[1].trim();
            }
          }
        }
        if (!name) {
          const local = email.split('@')[0] || '';
          name =
            local
              .split(/[._-]/)
              .filter(Boolean)
              .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
              .join(' ') || `Attendee ${idx + 1}`;
        }
        return {
          id: `att-split-${idx}`,
          name: name,
          email: email,
          company: companyList[idx] || (companyList.length === 1 ? companyList[0] : ''),
          jobTitle:
            meeting.guestTitle && !meeting.guestTitle.toLowerCase().includes('decision makers')
              ? meeting.guestTitle
              : '',
        };
      });
    }

    return [
      {
        id: meeting.leadId || 'guest-1',
        name: meeting.guestName || 'Invited Guest',
        email: meeting.guestEmail || '',
        company: meeting.guestCompany || '',
        jobTitle: meeting.guestTitle || '',
        stage: meeting.stage || null,
        leadScore: meeting.leadScore || null,
      },
    ];
  }, [meeting]);

  const campaignInfo = useMemo(() => {
    if (!meeting) return null;

    const brief = meeting.preMeetingBrief || meeting.pre_meeting_brief || {};
    let name =
      meeting.campaignName ||
      meeting.campaign_name ||
      brief.campaignName ||
      brief.campaign_name ||
      '';

    let id =
      meeting.campaignId || meeting.campaign_id || brief.campaignId || brief.campaign_id || '';

    // If explicit campaignName is missing, check if title contains campaign pattern (e.g. "Product Demo: Silicon Coast Talent Acquisition Campaign Cohort Session")
    if (!name && meeting.title) {
      const match =
        meeting.title.match(/:\s*(.+?)\s+Campaign\s+Cohort\s+Session/i) ||
        meeting.title.match(/:\s*(.+?)\s+Cohort/i) ||
        meeting.title.match(/:\s*(.+?)\s+Campaign/i);
      if (match && match[1]) {
        name = match[1].trim();
      }
    }

    const isGroup = Boolean(
      meeting.isGroupMeeting ||
      meeting.is_group_meeting ||
      brief.isGroupMeeting ||
      brief.is_group_meeting ||
      name ||
      id ||
      (Array.isArray(meeting.attendees) && meeting.attendees.length > 1) ||
      (Array.isArray(brief.attendees) && brief.attendees.length > 1) ||
      (meeting.title && meeting.title.toLowerCase().includes('cohort'))
    );

    if (!name && !isGroup) return null;

    const objective =
      liveCampaignObjective ||
      meeting.campaignObjective ||
      meeting.campaign_objective ||
      brief.campaignObjective ||
      brief.campaign_objective ||
      brief.objective ||
      (Array.isArray(brief.primaryObjectives) && brief.primaryObjectives.length > 0
        ? brief.primaryObjectives.join(' • ')
        : '') ||
      '';

    return {
      id,
      name: name || 'Target Campaign Cohort',
      isGroupMeeting: isGroup,
      objective: objective.trim(),
      attendeeCount: attendeesList.length,
    };
  }, [meeting, attendeesList, liveCampaignObjective]);

  const isGroupMeeting = Boolean(
    meeting?.isGroupMeeting ||
    meeting?.is_group_meeting ||
    campaignInfo?.isGroupMeeting ||
    (attendeesList && attendeesList.length > 1)
  );

  const handleGenerateAiBrief = async () => {
    if (!meeting || isGeneratingAiBrief) return;
    setIsGeneratingAiBrief(true);
    setAiBriefError(null);

    const wsId = meeting.workspaceId || meeting.workspace_id;
    const targetCmpId =
      meeting.campaignId ||
      meeting.campaign_id ||
      meeting.preMeetingBrief?.campaignId ||
      meeting.preMeetingBrief?.campaign_id;
    const targetCmpName =
      meeting.campaignName ||
      meeting.campaign_name ||
      meeting.preMeetingBrief?.campaignName ||
      meeting.preMeetingBrief?.campaign_name;

    try {
      const payload = {
        meetingId: meeting.id,
        campaignId: targetCmpId,
        campaignName: targetCmpName,
        meetingTitle: meeting.title,
        meetingType: meeting.meetingTypeName || meeting.meeting_type_name,
        attendees: attendeesList,
        hostName: meeting.hostName,
        hostEmail: meeting.hostEmail,
      };

      if (meetingsApi && typeof meetingsApi.generateMeetingAiBrief === 'function') {
        const result = await meetingsApi.generateMeetingAiBrief(wsId, payload, meeting.id);
        const briefPayload = result?.data || result;
        if (
          briefPayload &&
          typeof briefPayload === 'object' &&
          Object.keys(briefPayload).length > 0
        ) {
          setAiBrief(briefPayload);
        }
      }
    } catch (err) {
      console.error('Failed to generate real-time AI brief for group meeting:', err);
      setAiBriefError(err.message || 'Failed to generate AI brief from LLM');
    } finally {
      setIsGeneratingAiBrief(false);
    }
  };

  useEffect(() => {
    if (
      isGroupMeeting &&
      !aiBrief &&
      !isGeneratingAiBrief &&
      meeting?.id &&
      typeof meetingsApi?.generateMeetingAiBrief === 'function'
    ) {
      handleGenerateAiBrief();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.id, isGroupMeeting]);

  const handleCopyLink = () => {
    if (!meeting.conferencingUrl) return;
    navigator.clipboard?.writeText(meeting.conferencingUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyCta = () => {
    if (!aiBrief?.recommendedClosingCta) return;
    navigator.clipboard?.writeText(aiBrief.recommendedClosingCta);
    setCopiedCta(true);
    setTimeout(() => setCopiedCta(false), 2000);
  };

  const handleCopyFullBrief = () => {
    if (!aiBrief) return;
    const lines = [
      `=== AI STRATEGIC BRIEF: ${meeting.title || 'Cohort Session'} ===`,
      aiBrief.executiveSummary ? `\n[EXECUTIVE STRATEGY]\n${aiBrief.executiveSummary}` : '',
      Array.isArray(aiBrief.keyObjectives) && aiBrief.keyObjectives.length > 0
        ? `\n[OBJECTIVES]\n${aiBrief.keyObjectives.map((o) => `• ${o}`).join('\n')}`
        : '',
      Array.isArray(aiBrief.talkingPoints) && aiBrief.talkingPoints.length > 0
        ? `\n[KEY TALKING POINTS]\n${aiBrief.talkingPoints
            .map(
              (tp, i) =>
                `${i + 1}. [${tp.pillar || 'Point'}] ${tp.headline || ''}: ${tp.description || ''}`
            )
            .join('\n')}`
        : '',
      Array.isArray(aiBrief.anticipatedAudienceConcerns) &&
      aiBrief.anticipatedAudienceConcerns.length > 0
        ? `\n[OBJECTION HANDLING]\n${aiBrief.anticipatedAudienceConcerns
            .map((c) => `Q: ${c.concern}\nA: ${c.recommendedResponse}`)
            .join('\n\n')}`
        : '',
      Array.isArray(aiBrief.hostActionItems) && aiBrief.hostActionItems.length > 0
        ? `\n[ACTION PLAN]\n${aiBrief.hostActionItems.map((a) => `[ ] ${a}`).join('\n')}`
        : '',
      aiBrief.recommendedClosingCta ? `\n[CLOSING CTA]\n${aiBrief.recommendedClosingCta}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    navigator.clipboard?.writeText(lines);
    setCopiedFullBrief(true);
    setTimeout(() => setCopiedFullBrief(false), 2000);
  };

  const handleToggleBriefChecklist = (idx) => {
    setBriefChecklistDone((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleSaveNotes = async () => {
    if (isSavingNotes || isViewer) return;
    setIsSavingNotes(true);
    setSaveNotesSuccess(false);

    const currentRecap = meeting.postMeetingRecap || meeting.post_meeting_recap || {};
    const recapData = {
      ...currentRecap,
      repNotes: repNotes,
      heldAt: meeting.startTime,
      updatedAt: new Date().toISOString(),
    };

    try {
      if (typeof onSaveRecap === 'function') {
        await onSaveRecap(meeting.id, recapData);
      }
      setSaveNotesSuccess(true);
      setNotesViewMode('preview');
      setTimeout(() => {
        setSaveNotesSuccess(false);
      }, 2500);
    } catch (err) {
      console.error('Failed to save meeting notes:', err);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleConfirmCancel = () => {
    if (!cancelReason.trim()) return;
    onCancelMeeting(meeting.id, cancelReason);
    setShowCancelPrompt(false);
  };

  const formatDateFull = (iso) => {
    if (!iso) return 'Date TBD';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTimeRange = (startIso, endIso, durationMins = 30) => {
    if (!startIso) return 'Time TBD';
    const start = new Date(startIso);
    let end = endIso
      ? new Date(endIso)
      : new Date(start.getTime() + (Number(durationMins) || 30) * 60000);
    if (isNaN(end.getTime())) {
      end = new Date(start.getTime() + (Number(durationMins) || 30) * 60000);
    }
    const formatTime = (dt) =>
      dt
        .toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        .toLowerCase();

    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const formatMonthShort = (iso) => {
    if (!iso) return 'CAL';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'CAL';
    return d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  };

  const formatDayNumber = (iso) => {
    if (!iso) return '--';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '--';
    return String(d.getDate()).padStart(2, '0');
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  if (!meeting) return null;

  const isPast = isMeetingPast(meeting);
  const isInProgress = isMeetingInProgress(meeting);
  const statusKey = getEffectiveMeetingStatus(meeting);
  const canJoin = canJoinMeeting(meeting, currentTime);
  const startMs = meeting.startTime ? new Date(meeting.startTime).getTime() : 0;
  const msUntilStart = Math.max(0, startMs - currentTime);
  const isWithin15MinsOfStart = msUntilStart > 0 && msUntilStart <= 15 * 60 * 1000;
  const countdownToStartText = formatMeetingCountdown(msUntilStart);
  const statusLabels = {
    confirmed: 'Confirmed',
    in_progress: 'In Progress',
    needs_recap: 'Needs Recap',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  const statusFormatted =
    statusLabels[statusKey] ||
    statusKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const providerName = (
    meeting.conferencingProvider ||
    meeting.conferencing ||
    'Google Meet'
  ).replace(/_/g, ' ');

  const hasRescheduledTime = Boolean(
    meeting.originalStartTime &&
    meeting.startTime &&
    new Date(meeting.originalStartTime).getTime() !== new Date(meeting.startTime).getTime()
  );

  return (
    <div className="meetings-modal-overlay" onClick={onClose}>
      <div className="meetings-modal-container modern-cal" onClick={(e) => e.stopPropagation()}>
        {/* Top Minimal Bar */}
        <div className="cal-modal-topbar">
          <div className="cal-modal-status-wrap">
            <span className={`meeting-modal-status-badge ${statusKey}`}>
              <span className="status-dot"></span>
              {statusFormatted}
            </span>
            {meeting.meetingTypeName && (
              <span className="cal-modal-type-chip">{meeting.meetingTypeName}</span>
            )}
            {campaignInfo?.isGroupMeeting && (
              <span
                className="cal-modal-type-chip group-cohort"
                style={{
                  background: '#f5f3ff',
                  color: '#7c3aed',
                  borderColor: '#ddd6fe',
                  fontWeight: 600,
                }}
              >
                <i className="fas fa-users" style={{ marginRight: '4px' }}></i> Group Session
              </span>
            )}
          </div>
          <button
            type="button"
            className="meetings-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Header Title & Date Bar */}
        <div className="cal-modal-header">
          <h2 className="meetings-modal-title">{meeting.title || 'Scheduled Meeting'}</h2>

          {/* Schedule Hero Card & Reschedule Notice */}
          <div className="cal-schedule-hero-container">
            {/* If meeting was rescheduled, show a clean, elegant notice bar */}
            {hasRescheduledTime && (
              <div className="cal-reschedule-notice-bar cal-schedule-entry is-rescheduled-prev">
                <div className="cal-reschedule-notice-left">
                  <span className="cal-reschedule-notice-pill">
                    <i className="fas fa-history"></i> Was Scheduled
                  </span>
                  <span className="cal-reschedule-notice-text strikethrough">
                    {formatDateFull(meeting.originalStartTime)} •{' '}
                    {formatTimeRange(
                      meeting.originalStartTime,
                      meeting.originalEndTime,
                      meeting.durationMinutes
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Current active scheduled date hero card */}
            <div className="cal-schedule-hero-card cal-schedule-entry is-current">
              <div className="cal-date-badge active">
                <span className="cal-date-badge-month">{formatMonthShort(meeting.startTime)}</span>
                <span className="cal-date-badge-day">{formatDayNumber(meeting.startTime)}</span>
              </div>
              <div className="cal-schedule-hero-info">
                <div className="cal-schedule-hero-date-row">
                  <span className="cal-schedule-hero-date">
                    {formatDateFull(meeting.startTime)}
                  </span>
                  <span className="cal-schedule-duration-pill">
                    {meeting.durationMinutes || 30} mins
                  </span>
                  {hasRescheduledTime && (
                    <span className="cal-rescheduled-active-pill">
                      <i className="fas fa-calendar-check"></i> Rescheduled
                    </span>
                  )}
                </div>
                <div className="cal-schedule-hero-time-row">
                  <i className="far fa-clock"></i>
                  <span>
                    {formatTimeRange(meeting.startTime, meeting.endTime, meeting.durationMinutes)}
                  </span>
                  <span className="cal-schedule-tz-tag">GMT+8</span>
                </div>
              </div>
            </div>
          </div>

          {/* Join Call CTA Button / Ended / Locked Notice */}
          {isPast || statusKey === 'completed' ? (
            <div className="cal-join-banner is-ended">
              <div className="cal-join-btn is-ended" title="This meeting has already ended">
                <i className="fas fa-check-circle text-emerald"></i>
                <span>Meeting Completed</span>
              </div>
            </div>
          ) : (
            meeting.conferencingUrl &&
            statusKey !== 'cancelled' && (
              <div className="cal-join-banner">
                {statusKey === 'in_progress' || isInProgress ? (
                  <a
                    href={meeting.conferencingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cal-join-btn is-live"
                    title="Meeting is in progress - Join now!"
                  >
                    <i className="fas fa-video"></i>
                    <span>Join Now</span>
                  </a>
                ) : canJoin ? (
                  <a
                    href={meeting.conferencingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cal-join-btn"
                    title={`Join room with ${providerName}`}
                  >
                    <i className="fas fa-video"></i>
                    <span>
                      Join with {providerName}
                      {isWithin15MinsOfStart && ` (Starts in ${countdownToStartText})`}
                    </span>
                  </a>
                ) : (
                  <div
                    className="cal-join-btn is-locked"
                    title="Meeting room access opens 15 minutes before the scheduled start time"
                  >
                    <i className="fas fa-lock"></i>
                    <span>Join opens 15 mins before start</span>
                  </div>
                )}
                <button
                  type="button"
                  className="cal-copy-link-btn"
                  onClick={handleCopyLink}
                  title="Copy meeting URL"
                >
                  <i className={`fas ${copiedLink ? 'fa-check text-green' : 'fa-copy'}`}></i>
                  <span>{copiedLink ? 'Copied' : 'Copy link'}</span>
                </button>
              </div>
            )
          )}
        </div>

        {/* Minimal Tab Navigation */}
        <div className="meetings-modal-tabs">
          <button
            type="button"
            className={`meetings-modal-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            Details
          </button>

          {isGroupMeeting && (
            <button
              type="button"
              className={`meetings-modal-tab ${activeTab === 'ai_brief' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('ai_brief');
                if (!aiBrief && !isGeneratingAiBrief) {
                  handleGenerateAiBrief();
                }
              }}
            >
              <i className="fas fa-sparkles" style={{ marginRight: '6px', color: '#7c3aed' }}></i>
              AI Brief
            </button>
          )}

          <button
            type="button"
            className={`meetings-modal-tab ${activeTab === 'recap' ? 'active' : ''}`}
            onClick={() => setActiveTab('recap')}
          >
            Recap & Notes
            {meeting.status === 'needs_recap' && <span className="cal-tab-dot warning"></span>}
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="meetings-modal-content">
          {/* TAB 1: DETAILS */}
          {activeTab === 'info' && (
            <div className="cal-details-pane">
              {/* Campaign Context Section (for Group Meetings & Campaign Sessions) */}
              {campaignInfo && (
                <div className="cal-section">
                  <h4 className="cal-section-label">Campaign</h4>
                  <div className="cal-campaign-context-card">
                    <div className="cal-campaign-card-header">
                      <div className="cal-campaign-icon-avatar">
                        <i className="fas fa-bullhorn"></i>
                      </div>
                      <div className="cal-campaign-info-wrap">
                        <div className="cal-campaign-title-row">
                          <strong className="cal-campaign-title">{campaignInfo.name}</strong>
                          <span className="cal-campaign-leads-pill">
                            <i className="fas fa-users"></i> {campaignInfo.attendeeCount} Lead
                            {campaignInfo.attendeeCount === 1 ? '' : 's'} Involved
                          </span>
                        </div>
                        {campaignInfo.objective && (
                          <div className="cal-campaign-simple-obj">
                            <span className="cal-campaign-obj-label">Objective:</span>
                            <span className="cal-campaign-obj-text">{campaignInfo.objective}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Host Section */}
              <div className="cal-section">
                <h4 className="cal-section-label">Host</h4>
                <div className="cal-participants-list">
                  {(() => {
                    const matchedHost = (hosts || []).find(
                      (h) =>
                        (meeting.hostId && h.id === meeting.hostId) ||
                        (meeting.hostEmail &&
                          h.email &&
                          h.email.toLowerCase() === meeting.hostEmail.toLowerCase())
                    );

                    const hostNameDisplay =
                      matchedHost?.name ||
                      (currentUser &&
                      ((currentUser.email &&
                        meeting.hostEmail &&
                        currentUser.email.toLowerCase() === meeting.hostEmail.toLowerCase()) ||
                        (currentUser.id && currentUser.id === meeting.hostId))
                        ? currentUser.name || currentUser.fullName || currentUser.username
                        : null) ||
                      (meeting.hostName &&
                      meeting.hostName !== 'Unassigned' &&
                      !meeting.hostName.includes('@')
                        ? meeting.hostName
                        : null) ||
                      (meeting.hostEmail ? meeting.hostEmail.split('@')[0] : 'Workspace Member');

                    return (
                      <div className="cal-participant-row">
                        <div className="cal-avatar host">{getInitials(hostNameDisplay)}</div>
                        <div className="cal-participant-info">
                          <div className="cal-participant-name-line">
                            <span className="cal-name">{hostNameDisplay}</span>
                            <span className="cal-role-tag host">Host</span>
                          </div>
                          {meeting.hostEmail && (
                            <div className="cal-email-text">
                              <i className="far fa-envelope"></i>
                              <span>{meeting.hostEmail}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Participants Section */}
              <div className="cal-section">
                <h4 className="cal-section-label">
                  Participants {attendeesList.length > 1 ? `(${attendeesList.length})` : ''}
                </h4>
                <div className="cal-participants-list">
                  {/* Attendees / Guests */}
                  {attendeesList.map((attendee, idx) => {
                    const attName = attendee.name || attendee.guestName || `Attendee ${idx + 1}`;
                    const attTitle =
                      attendee.jobTitle || attendee.title || attendee.guestTitle || '';
                    const attCompany = attendee.company || attendee.guestCompany || '';
                    const attEmail = attendee.email || attendee.guestEmail || '';
                    const attStage = attendee.stage || attendee.pipelineStage || '';

                    return (
                      <div
                        className="cal-participant-row"
                        key={attendee.id || attendee.crmId || attendee.email || idx}
                      >
                        <div className="cal-avatar guest">{getInitials(attName)}</div>
                        <div className="cal-participant-info">
                          <div className="cal-participant-name-line">
                            <span className="cal-name">{attName}</span>
                            <span className="cal-role-tag guest">Guest</span>
                            {attStage && (
                              <span
                                className="cal-stage-pill"
                                style={{
                                  marginLeft: '4px',
                                  fontSize: '10.5px',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  background: '#f1f5f9',
                                  border: '1px solid #e2e8f0',
                                  color: '#475569',
                                  fontWeight: 500,
                                }}
                              >
                                {attStage}
                              </span>
                            )}
                          </div>
                          {(attTitle || attCompany) && (
                            <div className="cal-participant-sub">
                              {attTitle && <span>{attTitle}</span>}
                              {attTitle && attCompany && <span className="cal-sub-dot">•</span>}
                              {attCompany && <span>{attCompany}</span>}
                            </div>
                          )}
                          {attEmail && (
                            <a href={`mailto:${attEmail}`} className="cal-email-link">
                              <i className="far fa-envelope"></i>
                              <span>{attEmail}</span>
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Deal & Fit Metrics (1-on-1 individual meetings only; omitted for group meetings) */}
              {!isGroupMeeting &&
                (meeting.leadScore ||
                  meeting.dealEstimate ||
                  meeting.postMeetingRecap?.recommendedStage) && (
                  <div className="cal-section">
                    <h4 className="cal-section-label">Lead & Pipeline Context</h4>
                    <div className="cal-metrics-row">
                      {meeting.leadScore && (
                        <div className="cal-metric-chip">
                          <span className="cal-metric-label">Lead Score</span>
                          <span className="cal-metric-val score">
                            <i className="fas fa-star text-amber"></i> {meeting.leadScore}/100
                          </span>
                        </div>
                      )}
                      {meeting.dealEstimate && (
                        <div className="cal-metric-chip">
                          <span className="cal-metric-label">Estimated Value</span>
                          <span className="cal-metric-val">{meeting.dealEstimate}</span>
                        </div>
                      )}
                      {meeting.postMeetingRecap?.recommendedStage && (
                        <div className="cal-metric-chip">
                          <span className="cal-metric-label">Pipeline Stage</span>
                          <span className="cal-metric-val">
                            {meeting.postMeetingRecap.recommendedStage}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Guest Intake Notes */}
              {meeting.guestNotes && (
                <div className="cal-section">
                  <h4 className="cal-section-label">Booking Note from Guest</h4>
                  <div className="cal-note-quote">
                    <p className="info-notes-quote">"{meeting.guestNotes}"</p>
                  </div>
                </div>
              )}

              {/* Cancelled Banner */}
              {meeting.status === 'cancelled' && (
                <div className="cal-cancelled-box">
                  <div className="cal-cancelled-icon-wrap">
                    <i className="fas fa-ban"></i>
                  </div>
                  <div className="cal-cancelled-body">
                    <div className="cal-cancelled-title">Meeting Cancelled</div>
                    {meeting.cancellationReason && (
                      <div className="cal-cancelled-reason">
                        <span className="cal-cancelled-reason-label">Reason:</span> "
                        {meeting.cancellationReason}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: AI BRIEF (for Group Meetings) */}
          {activeTab === 'ai_brief' &&
            (() => {
              const briefStart =
                Array.isArray(aiBrief?.howToStart) && aiBrief.howToStart.length > 0
                  ? aiBrief.howToStart
                  : aiBrief?.executiveSummary
                    ? [aiBrief.executiveSummary]
                    : [];

              const briefDo =
                Array.isArray(aiBrief?.whatToDo) && aiBrief.whatToDo.length > 0
                  ? aiBrief.whatToDo
                  : Array.isArray(aiBrief?.talkingPoints) && aiBrief.talkingPoints.length > 0
                    ? aiBrief.talkingPoints.map((t) =>
                        typeof t === 'object'
                          ? `${t.headline ? `${t.headline}: ` : ''}${t.description || ''}`
                          : String(t)
                      )
                    : Array.isArray(aiBrief?.keyObjectives)
                      ? aiBrief.keyObjectives
                      : [];

              const briefNotes =
                Array.isArray(aiBrief?.importantNotes) && aiBrief.importantNotes.length > 0
                  ? aiBrief.importantNotes
                  : Array.isArray(aiBrief?.anticipatedAudienceConcerns) &&
                      aiBrief.anticipatedAudienceConcerns.length > 0
                    ? aiBrief.anticipatedAudienceConcerns.map((c) =>
                        typeof c === 'object'
                          ? `${c.concern}${c.recommendedResponse ? ` → ${c.recommendedResponse}` : ''}`
                          : String(c)
                      )
                    : [];

              const briefClose = aiBrief?.closingNextStep || aiBrief?.recommendedClosingCta || '';

              return (
                <div className="cal-ai-brief-pane">
                  <div className="cal-ai-brief-section">
                    {/* Simple Header */}
                    <div className="cal-ai-brief-simple-header">
                      <div className="cal-ai-brief-title-wrap">
                        <h4 className="cal-section-label" style={{ marginBottom: 0 }}>
                          <i className="fas fa-sparkles text-purple"></i> AI Brief
                        </h4>
                        <span className="cal-ai-brief-live-badge">
                          <span className="cal-ai-brief-live-dot"></span> Real-Time LLM Analysis
                        </span>
                      </div>
                      <button
                        type="button"
                        className="cal-ai-brief-refresh-btn"
                        onClick={() => handleGenerateAiBrief()}
                        disabled={isGeneratingAiBrief}
                        title="Re-run real-time analysis"
                      >
                        <i
                          className={`fas fa-arrows-rotate ${isGeneratingAiBrief ? 'fa-spin' : ''}`}
                        ></i>
                        <span>{isGeneratingAiBrief ? 'Analyzing...' : 'Refresh'}</span>
                      </button>
                    </div>

                    {isGeneratingAiBrief ? (
                      <div className="cal-ai-brief-loading-simple">
                        <i className="fas fa-sparkles fa-spin text-purple"></i>
                        <span>Generating real-time brief from campaign data...</span>
                      </div>
                    ) : aiBriefError ? (
                      <div className="cal-ai-brief-error-simple">
                        <span>{aiBriefError}</span>
                        <button
                          type="button"
                          className="cal-ai-brief-retry-link"
                          onClick={() => handleGenerateAiBrief()}
                        >
                          Retry
                        </button>
                      </div>
                    ) : aiBrief ? (
                      <div className="cal-ai-brief-simple-cards">
                        {/* 1. How to Start */}
                        {briefStart.length > 0 && (
                          <div className="cal-ai-simple-card">
                            <div className="cal-ai-simple-card-head">
                              <i className="fas fa-play-circle text-purple"></i>
                              <strong>How to Start</strong>
                            </div>
                            <ul className="cal-ai-simple-list">
                              {briefStart.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* 2. What to Do */}
                        {briefDo.length > 0 && (
                          <div className="cal-ai-simple-card">
                            <div className="cal-ai-simple-card-head">
                              <i className="fas fa-list-check text-blue"></i>
                              <strong>What to Do</strong>
                            </div>
                            <ul className="cal-ai-simple-list">
                              {briefDo.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* 3. Important Notes */}
                        {briefNotes.length > 0 && (
                          <div className="cal-ai-simple-card">
                            <div className="cal-ai-simple-card-head">
                              <i className="fas fa-circle-exclamation text-amber"></i>
                              <strong>Important Notes</strong>
                            </div>
                            <ul className="cal-ai-simple-list">
                              {briefNotes.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* 4. Closing Next Step */}
                        {briefClose && (
                          <div className="cal-ai-simple-card closing">
                            <div className="cal-ai-simple-card-head">
                              <i className="fas fa-flag-checkered text-emerald"></i>
                              <strong>Closing Next Step</strong>
                            </div>
                            <p className="cal-ai-simple-close-text">{briefClose}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="cal-ai-brief-empty-simple">
                        <p>
                          Generate a quick brief based on campaign objectives and attendee roles.
                        </p>
                        <button
                          type="button"
                          className="cal-ai-brief-refresh-btn"
                          onClick={() => handleGenerateAiBrief()}
                        >
                          <i className="fas fa-sparkles"></i> Generate Brief
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {/* TAB 3: RECAP & NOTES */}
          {activeTab === 'recap' && (
            <div
              className={`cal-notes-container ${notesViewMode === 'write' ? 'is-write' : 'is-preview'}`}
            >
              {notesViewMode === 'write' ? (
                <>
                  <div className="cal-notes-header-bar">
                    <div className="cal-notes-mode-indicator">
                      <i className="fas fa-edit"></i>
                      <span>Meeting Notes</span>
                      {notesWordCount > 0 && (
                        <span className="cal-notes-count">({notesWordCount} words)</span>
                      )}
                    </div>

                    <div className="cal-notes-header-actions">
                      {hasNotesContent && (
                        <button
                          type="button"
                          className="cal-notes-cancel-btn"
                          onClick={() => setNotesViewMode('preview')}
                          title="Cancel editing and return to preview"
                        >
                          Cancel
                        </button>
                      )}
                      {!isViewer && (
                        <button
                          type="button"
                          className="cal-notes-save-btn"
                          onClick={handleSaveNotes}
                          disabled={isSavingNotes}
                        >
                          {isSavingNotes ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i> Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save"></i> Save Notes
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="cal-notes-body">
                    <ReactQuill
                      theme="snow"
                      value={repNotes}
                      onChange={setRepNotes}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={
                        isViewer
                          ? 'No notes recorded for this meeting.'
                          : 'Type your meeting notes here...\n\n• Key discussion takeaways\n• Client questions / requirements\n• Action items & next steps'
                      }
                      readOnly={isViewer}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="cal-notes-header-bar preview-header">
                    <div className="cal-notes-mode-indicator">
                      <i className="far fa-file-alt"></i>
                      <span>{isViewer ? 'Read-Only Notes' : 'Meeting Notes'}</span>
                      {notesWordCount > 0 && (
                        <span className="cal-notes-count">({notesWordCount} words)</span>
                      )}
                    </div>

                    <div className="cal-notes-header-actions">
                      {!isViewer && (
                        <button
                          type="button"
                          className="cal-notes-edit-btn"
                          onClick={() => setNotesViewMode('write')}
                          title="Edit meeting notes"
                        >
                          <i className="fas fa-pencil-alt"></i>
                          <span>Edit</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="cal-notes-body preview-body">
                    {hasNotesContent ? (
                      <ReactQuill
                        theme="snow"
                        value={renderPreviewContent(repNotes)}
                        readOnly={true}
                        modules={{ toolbar: false }}
                      />
                    ) : (
                      <div className="cal-notes-preview-empty">
                        <i className="far fa-sticky-note"></i>
                        <p>No notes written yet.</p>
                        {!isViewer && (
                          <button
                            type="button"
                            className="cal-notes-switch-write-btn"
                            onClick={() => setNotesViewMode('write')}
                          >
                            <i className="fas fa-plus"></i> Add Notes
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer Actions (Delete / Reschedule / Cancel) */}
        <div className="cal-modal-footer">
          {!canManageMeeting ? (
            <div className="cal-footer-right-actions" style={{ marginLeft: 'auto' }}>
              <button type="button" className="cal-footer-btn" onClick={onClose}>
                Close
              </button>
            </div>
          ) : showDeletePrompt ? (
            <div className="cal-footer-delete-confirm">
              <div className="cal-delete-prompt-text">
                <i className="fas fa-trash-alt text-rose"></i>
                <span>Permanently delete this meeting?</span>
              </div>
              <div className="cal-footer-right-actions">
                <button
                  type="button"
                  className="cal-footer-btn"
                  onClick={() => setShowDeletePrompt(false)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="cal-footer-btn danger-solid"
                  onClick={() => {
                    if (onDeleteMeeting) {
                      onDeleteMeeting(meeting.id);
                    }
                    if (onClose) {
                      onClose();
                    }
                  }}
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="cal-footer-btn delete"
                onClick={() => setShowDeletePrompt(true)}
                title="Permanently delete this meeting"
              >
                <i className="far fa-trash-alt"></i>
                <span>Delete</span>
              </button>

              <div className="cal-footer-right-actions">
                {(statusKey === 'confirmed' || statusKey === 'in_progress') &&
                  !isPast &&
                  !showCancelPrompt && (
                    <>
                      <button
                        type="button"
                        className={`cal-footer-btn ${canJoin || isInProgress || statusKey === 'in_progress' ? 'is-locked-action' : ''}`}
                        onClick={() => {
                          if (canJoin || isInProgress || statusKey === 'in_progress') {
                            setShowRescheduleLockedPrompt(true);
                            return;
                          }
                          onReschedule(meeting);
                        }}
                        title={
                          canJoin || isInProgress || statusKey === 'in_progress'
                            ? 'Rescheduling is locked because meeting access is already open or in progress'
                            : 'Reschedule this meeting'
                        }
                      >
                        Reschedule
                      </button>
                      <button
                        type="button"
                        className="cal-footer-btn danger"
                        onClick={() => setShowCancelPrompt(true)}
                      >
                        Cancel Meeting
                      </button>
                    </>
                  )}
              </div>
            </>
          )}
        </div>

        {/* Reschedule Locked Reminder Modal Popup */}
        {showRescheduleLockedPrompt && (
          <div
            className="cal-cancel-popup-overlay"
            onClick={() => setShowRescheduleLockedPrompt(false)}
          >
            <div className="cal-cancel-popup-card" onClick={(e) => e.stopPropagation()}>
              <div className="cal-cancel-popup-header">
                <div
                  className="cal-cancel-popup-icon-wrap"
                  style={{ background: '#fef3c7', color: '#d97706' }}
                >
                  <i className="fas fa-lock"></i>
                </div>
                <div className="cal-cancel-popup-title-wrap">
                  <h3 className="cal-cancel-popup-title">Rescheduling Not Allowed</h3>
                  <p className="cal-cancel-popup-subtitle">
                    This meeting is already open to join or currently in progress.
                  </p>
                </div>
                <button
                  type="button"
                  className="cal-cancel-popup-close"
                  onClick={() => setShowRescheduleLockedPrompt(false)}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div
                style={{
                  padding: '4px 24px 20px',
                  fontSize: '13px',
                  color: '#475569',
                  lineHeight: '1.55',
                }}
              >
                Because the meeting access window is already open (or in progress), rescheduling is
                locked to avoid attendee confusion and missed sessions. If you cannot attend, you
                can cancel the meeting or join the call now.
              </div>

              <div className="cal-cancel-popup-footer">
                <button
                  type="button"
                  className="cal-cancel-popup-btn secondary cal-btn-text"
                  onClick={() => setShowRescheduleLockedPrompt(false)}
                >
                  Dismiss
                </button>
                {meeting.conferencingUrl && (
                  <a
                    href={meeting.conferencingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cal-cancel-popup-btn danger cal-btn-danger"
                    style={{
                      background: '#2563eb',
                      borderColor: '#2563eb',
                      color: '#fff',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => setShowRescheduleLockedPrompt(false)}
                  >
                    <i className="fas fa-video" style={{ marginRight: '6px' }}></i> Join Meeting Now
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Confirmation Modal Popup */}
        {showCancelPrompt && (
          <div className="cal-cancel-popup-overlay" onClick={() => setShowCancelPrompt(false)}>
            <div className="cal-cancel-popup-card" onClick={(e) => e.stopPropagation()}>
              <div className="cal-cancel-popup-header">
                <div className="cal-cancel-popup-icon-wrap">
                  <i className="fas fa-calendar-times"></i>
                </div>
                <div className="cal-cancel-popup-title-wrap">
                  <h3 className="cal-cancel-popup-title">Cancel Meeting</h3>
                  <p className="cal-cancel-popup-subtitle">
                    Are you sure you want to cancel this scheduled session? All attendees will be
                    notified.
                  </p>
                </div>
                <button
                  type="button"
                  className="cal-cancel-popup-close"
                  onClick={() => setShowCancelPrompt(false)}
                  title="Close"
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="cal-cancel-popup-body">
                <label className="cal-cancel-popup-label">
                  Reason for Cancellation <span style={{ color: '#e11d48' }}>*</span>
                </label>
                <textarea
                  rows={3}
                  className="cal-cancel-popup-textarea"
                  placeholder="Provide a reason for cancelling this meeting (e.g. client requested reschedule, conflict, etc.)..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="cal-cancel-popup-footer">
                <button
                  type="button"
                  className="cal-cancel-popup-btn secondary cal-btn-text"
                  onClick={() => setShowCancelPrompt(false)}
                >
                  Keep Meeting
                </button>
                <button
                  type="button"
                  className="cal-cancel-popup-btn danger cal-btn-danger"
                  onClick={handleConfirmCancel}
                  disabled={!cancelReason.trim()}
                >
                  <i className="fas fa-ban"></i> Confirm Cancellation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
